import angular from 'angular';

import nxModule from 'nxModule';
import {HttpService} from 'shared/utils/httpService';

interface PartUploadPartResponse {
  uploadId: string;
  eTag: string;
  partNumber: number;
  storageKey: string;
}

interface PartUploadCompleteResponse {
  attachedFileId: number;
  fileName: string;
}

interface FilePartDetail {
  partNumber: number;
  eTag: string;
}

export interface PartUploadProgress {
  partsUploaded: number;
  totalParts: number;
  percent: number;
}

const PART_SIZE = 10 * 1024 * 1024; // 10MB

export class PartUploadService {
  constructor(private http: HttpService) {}

  async uploadFile(
    file: File,
    onProgress?: (progress: PartUploadProgress) => void
  ): Promise<PartUploadCompleteResponse> {
    const totalParts = Math.ceil(file.size / PART_SIZE);
    const parts: FilePartDetail[] = [];
    let uploadId: string | undefined;
    let storageKey: string | undefined;

    for (let i = 0; i < totalParts; i++) {
      const start = i * PART_SIZE;
      const end = Math.min(start + PART_SIZE, file.size);
      const part = file.slice(start, end);
      const partNumber = i + 1;

      const formData = new FormData();
      formData.append('file', part);
      formData.append('partNumber', String(partNumber));
      formData.append('fileName', file.name);
      if (file.type) {
        formData.append('contentType', file.type);
      }
      if (uploadId) {
        formData.append('uploadId', uploadId);
      }
      if (storageKey) {
        formData.append('storageKey', storageKey);
      }

      const response = await this.http.post<PartUploadPartResponse>(
        '/files/part',
        formData,
        {
          transformRequest: angular.identity,
          headers: {'Content-Type': undefined},
          nxLoaderSkip: true,
        }
      ).toPromise();

      // Capture uploadId and storageKey from the first response
      if (!uploadId) {
        uploadId = response.uploadId;
        storageKey = response.storageKey;
      }

      // Store eTag exactly as returned — do not modify
      parts.push({partNumber: response.partNumber, eTag: response.eTag});

      if (onProgress) {
        onProgress({
          partsUploaded: partNumber,
          totalParts,
          percent: Math.round((partNumber / totalParts) * 100),
        });
      }
    }

    const completeResponse = await this.http.post<PartUploadCompleteResponse>(
      `/files/part/${uploadId}/complete`,
      {
        storageKey,
        fileName: file.name,
        contentType: file.type,
        fileSizeInBytes: file.size,
        parts,
        allowDuplicates: true,
      },
      {
        nxLoaderSkip: true,
      }
    ).toPromise();

    return completeResponse;
  }
}

nxModule.service('partUploadService', PartUploadService);
